import React, { useEffect, useRef } from "react"; // Added useRef
import "../styles/AboutSection.css";
import {
  MdSecurity,
  MdBolt,
  MdBrush,
  // MdVisibility,
  // MdHandshake,
} from "react-icons/md";
import { useTranslation } from "react-i18next";

const iconMap = {
  MdSecurity: <MdSecurity />,
  MdBolt: <MdBolt />,
  MdBrush: <MdBrush />,
  // Extendable
};

const AboutSection = () => {
  const { t } = useTranslation();
  const values = t("home.about.values", { returnObjects: true });
  // Use a ref for the section to avoid issues with document.querySelector if component re-renders fast
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentSection = sectionRef.current; // Use the ref's current value
    if (!currentSection) return;

    const cards = currentSection.querySelectorAll(".value-card");

    const animateCards = () => {
      cards.forEach((card) => {
        const fill = card.getAttribute("data-fill");
        const fillBar = card.querySelector(".fill-bar");

        // Ensure fillBar and fill value are valid before trying to set width
        if (fillBar && fill && !isNaN(parseFloat(fill))) {
          fillBar.style.width = `${fill}%`;
        } else if (fillBar) {
          // Fallback if data-fill is missing or invalid
          fillBar.style.width = `0%`;
          console.warn("Invalid or missing data-fill for card:", card);
        }

        const counter = card.querySelector(".value-count");
        // Ensure counter and its attributes are valid
        if (counter) {
          const target = +counter.getAttribute("data-target");
          const unit = counter.getAttribute("data-unit") || ""; // Ensure unit is a string
          let count = 0;

          if (!isNaN(target)) {
            const updateCount = () => {
              const increment = Math.max(1, target / 50); // Ensure increment is at least 1 or based on target
              if (count < target) {
                count = Math.min(count + increment, target); // Ensure count doesn't overshoot
                counter.innerText = Math.ceil(count) + unit;
                setTimeout(updateCount, 20); // Be cautious with many simultaneous timeouts
              } else {
                counter.innerText = target + unit;
              }
            };
            updateCount();
          } else {
            console.warn("Invalid or missing data-target for counter:", counter);
            counter.innerText = "0" + unit;
          }
        }
      });
    };

    const resetCards = () => {
      cards.forEach((card) => {
        const fillBar = card.querySelector(".fill-bar");
        if (fillBar) fillBar.style.width = `0%`;

        const counter = card.querySelector(".value-count");
        if (counter) {
          const unit = counter.getAttribute("data-unit") || "";
          counter.innerText = "0" + unit;
        }
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCards();
          } else {
            resetCards();
          }
        });
      },
      { threshold: 0.4 } // Trigger when 40% of the element is visible
    );

    observer.observe(currentSection);

    return () => {
      observer.unobserve(currentSection);
    };
  }, [t]); // Added 't' to dependency array. If 'values' can change based on 't', this ensures effect re-runs.

  if (!Array.isArray(values) || values.length === 0) {
    // Added check for empty array
    console.warn("No values found for AboutSection or values is not an array.");
    return null;
  }

  return (
    <section className="about-eqera" id="about" ref={sectionRef}> {/* Attach ref here */}
      <div className="container about-inner">
        <div className="about-text">
          <h2>{t("home.about.title")}</h2>
          <p>{t("home.about.description")}</p>
        </div>

        <div className="about-values">
          {values.map((val, i) => (
            <div
              className="value-card"
              key={val.title || i} // Prefer a stable key like val.id or val.title if unique
              data-fill={val.target} // This is where the fill percentage comes from
            >
              <div className="fill-bar"></div> {/* This is the element that gets filled */}
              <div className="value-content">
                <div className="value-title">
                  {iconMap[val.icon] || null} {val.title}
                </div>
                <div
                  className="value-count"
                  data-target={val.target}
                  data-unit={val.unit || ""} // Ensure unit is always a string
                >
                  0{val.unit || ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;